import { ErrorCode } from 'react-dropzone';
import { NotificationCanal, NotificationPriority, NotificationType } from './notifications';
import { AirtableAccountManager, CRAStatus, SupplierInvoiceStatus } from './airtable';
import { Upload } from 'graphql-upload';
import { SmartFields } from './ports/esign';
import { BusinessType } from './domain';

export const AddressCountryCodeEU = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
];

export type ATSOrigin = 'pipefy' | 'hiresweet' | 'airtable';

export interface BrowserUploadFile extends Partial<File> {
  url?: string;
  path?: string;
  errors?: { message: string; code: ErrorCode | string }[];
}

export type DataValue = {
  firstname: string;
  lastname: string;
  email: string;
  jobTitle: string;
  linkedIn?: string;
};

export enum ActivityDayType {
  'nbDaysWorked' = 'nbDaysWorked',
  'nbDaysMarkupWorked' = 'nbDaysMarkupWorked',
  'nbDaysMarkup2Worked' = 'nbDaysMarkup2Worked',
  'astreinte' = 'astreinte',
}

export type ActivityDayDetails = {
  id: number;
  day: string;
  date: string;
  isHoliday: boolean;
  isSunday: boolean;
  isSaturday: boolean;
  isWeekEndDay: boolean;
  dayWorked?: number;
  checked: boolean;
  comment?: string;
  astreinte?: number;
};

export type ActivityNbDayDetails = {
  [key: number]: {
    duration: number;
    comment?: string;
    isAstreinte: number;
  };
};

export type DataCSV = {
  data: {
    line: number | string;
    firstname: string;
    lastname: string;
    email: string;
    jobTitle: string;
    linkedIn?: string | undefined;
  };
};

export interface ITag {
  value: string;
  count?: number;
}

export type Paginate = {
  take: number;
  skip: number;
};

export type Item = {
  id: string;
  minLevel: number;
  label: string;
};

// ------------ api.bloomers -------------

export interface IMissionQueryInput {
  research?: string;
}

export type BrowserFieldOrFile = BrowserUploadFile | string;
export type ServerFieldOrFile = Upload | string;

export type BloomerFiles = 'credentialID';

// carefull this interfaceis for client side only
export interface BrowserBloomer extends Omit<IBloomer, 'credentialID'> {
  credentialID?: BrowserFieldOrFile;
}

export interface IBloomer {
  isNew?: boolean;
  notification?: INotification[];
  id?: string;
  fullname?: string;
  firstname: string;
  lastname: string;
  email: string;
  secondaryEmail?: string;
  phoneNumber?: string;
  slack?: boolean;
  accountManager?: IAccountManager;
  accountManagerId?: string;
  accountManagerName?: string;
  mission?: IMission[];
  recordId?: string;
  credentialID?: ServerFieldOrFile;
  invitation?: Invitation;
  communicationType: NotificationCanal;
  pipefyContactId?: string;
  hiresweetContactId?: string;
}

export interface IAccountManager extends Omit<AirtableAccountManager, 'bloomers'> {
  bloomers: BrowserBloomer[];
}

// TODO
export interface IMission {
  contracts?: string[];
  enableCRAUpload?: boolean;
  jobId?: string;
  bdcExpirationDate?: string;
  bdc?: string;
  havePreviousMonthActivity?: string[] | undefined;
  id?: number;
  label?: string;
  beginMission: string;
  endMission: string;
  statusBloomer?: string;
  statusClient?: string;
  bloomer: BrowserBloomer;
  activity: IActivity[];
  activityRecordId: string[];
  pricing: IPricing[];
  pricingRecordId: string[];
  clientSociety?: ISociety;
  clientSocietyRecordId?: string;
  clientBillingSociety?: ISociety;
  clientBillingSocietyRecordId?: string;
  bloomerBillingSociety: ISociety[];
  bloomerBillingSocietyRecordId: string[];
  contactOperations?: IContact;
  contactOperationsRecordId?: string;
  billingContacts: IContact[];
  billingContactsRecordId: string[];
  recordId?: string;
  isActive?: boolean;
  externalId?: string;
  bloomerSocietyName: string[];
  clientSocietyName: string[];
  clientBillingSocietyName?: string[];
  bloomerRecordId: string[];
  bloomerPaydateDays?: number;
  clientPaydateDays?: number;
  type: string;
  reasearchField?: string;
  bloomerFullName?: string[];
  accountManager?: IAccountManager[];
  CSM?: IAccountManager;
  CSMRecordId: string[];
  contractType?: string;
  createdAt?: string;
  replacement: boolean;
  hoursPerDays: number;
  origin?: ATSOrigin;
  enableBusinessReferalNpsClient: boolean;
  enableBusinessReferalNpsBloomer: boolean;
}

export type SocietyFiles =
  | 'KBIS'
  | 'URSSAFVigilanceCertificate'
  | 'URSSAFVigilanceCertificate'
  | 'RCPVigilanceCertificate'
  | 'RCPVigilanceCertificate';

export interface ISociety {
  addressPart2?: string;
  id?: string;
  name: string;
  status?: string;
  siret: string;
  isClient?: boolean;
  bloomers?: string[];
  billingContact?: IContact;
  mission?: IMission[];
  recordId?: string;
  KBIS?: string;
  KbisExpirationDate?: string;
  URSSAFVigilanceCertificate?: string;
  URSSAFVCExpirationDate?: string;
  URSSAFVCUploadDate?: string;
  RCPVigilanceCertificate?: string;
  RCPExpirationDate?: string;
  IBAN?: string;
  BIC?: string;
  portage?: boolean;
  paydateDays?: number;
  bloomerBillingMission?: IMission[];
  tva?: string;
  addressName?: string;
  addressPart1?: string;
  addressZip?: string;
  addressTown?: string;
  addressCountryCode?: string | null;
  addressCountry?: string;
  externalId?: string;
  pitch?: string;
  description?: string;
  interviewTips?: string;
  internalOrganization?: string;
  phone?: string;
}

export interface IPricing {
  id?: string;
  startDate?: Date;
  purchasePricePerDayWithoutTaxes: number;
  salePricePerDayWithoutTaxes: number;
  marginAmount?: string;
  marginRate?: string;
  taxes: number;
  activity?: IActivity[];
  mission?: IMission[];
  recordId?: string;
  astreinteCoef?: number;
  recordIdMission: string[];
}

export type ActivityFiles = 'urlCraS3' | 'urlInvoicesS3' | 'urlAdditionalInvoicesS3';

// TODO
export interface IActivity {
  jobId?: string;
  id?: string;
  month: string;
  nbDaysWorked: number;
  nbDaysMarkupWorked: number;
  nbDaysMarkup2Worked: number;
  nbDaysDetails: string;
  astreinte?: number;
  bloomer: BrowserBloomer[];
  mission?: IMission;
  validated: boolean;
  signing?: boolean;
  recordId: string;
  signingDocumentId?: string;
  billed: boolean;
  signedCount?: number;
  urlCraS3?: string;
  urlInvoicesS3?: string;
  urlAdditionalInvoicesS3?: string;
  bloomerFullname: string[];
  clientMission: string[];
  bloomerRecordId: string[];
  type?: string[];
  signingEmbedBloomerId?: string;
  signingEmbedClientId?: string;
  recordIdMission: string;
  clientSocietyName: string;
  payed: boolean;
  dateUploadInvoice?: string;
  CRAStatus: CRAStatus;
  lastError?: string;
  lastSupplierError?: string;
  externalCustomerInvoiceId?: string;
  externalCustomerInvoiceMarkupId?: string;
  externalSupplierInvoiceId?: string;
  supplierInvoiceStatus?: SupplierInvoiceStatus;
  hoursPerDays: number;
}

// TODO : move to airtable file with ISociety / IMission to be created as Airtable types
export interface IContact {
  id?: string;
  fullname?: string;
  firstname: string;
  lastname: string;
  phoneNumber?: string;
  email: string;
  nameJob: string;
  isBilling: boolean;
  isOperational?: boolean | null;
  society?: ISociety[];
  mission?: IMission[];
  recordId?: string;
  externalId: string;
  communicationType: NotificationCanal;
}

export type MissionContractType = 'bloomaysContract' | 'devis' | 'clientContract';

export interface IMissionCardDetails {
  label?: string;
  recordId: string;
  beginMission: string;
  endMission: string;
  purchasePricePerDayWithoutTaxes?: number;
  salePricePerDayWithoutTaxes?: number;
  taxes?: number;
  CSM?: string;
  contractType?: MissionContractType;
  replacement: boolean | null;
  shouldBeCountAsDeal: boolean;
  isActive: boolean;
  bloomerPaydateDays?: number;
  clientPaydateDays?: number;
  type: BusinessType;
  prices?: IMissionCardPricing[];
  clientBillingSociety?: ISociety;
  clientSociety?: ISociety;
  pricing?: IPricing;
  bloomerBillingSociety?: ISociety[];
  contactOperations?: IContact;
  billingContacts?: IContact;
  bloomer: IBloomer;
  hoursPerDays: number;
  origin?: ATSOrigin;
  enableCRAUpload?: boolean;
  enableBusinessReferalNpsClient: boolean;
  enableBusinessReferalNpsBloomer: boolean;
}

export interface IMissionCardPricing {
  startDate?: Date;
  purchasePricePerDayWithoutTaxes: number;
  salePricePerDayWithoutTaxes: number;
  taxes: number;
  recordId?: string;
  astreinteCoef?: number;
}

export type Invitation = {
  recordId: string;
  email: string;
  token: string;
  jobTitle: string;
  clientSocietyName?: string;
  purchasePricePerDayWithoutTaxes?: number;
  missionStartDate?: Date;
  missionEndDate?: Date;
  firstname: string;
  lastname: string;
  mission: string[];
  status: string;
  isNew?: boolean;
};

export interface ISorting {
  field: string;
  direction: string;
}

export interface IPagination {
  limit: number;
  offset: number;
}

export interface Recommendations {
  name: string;
  comment: string;
}

export interface IEducations {
  title: string;
  degree: string;
  url: string;
  description: string;
  fieldOfStudy: string;
  beginDate: Date;
  endDate: Date;
}

export interface IExperiences {
  title: string;
  description: string;
  companyName: string;
  url: string;
  beginDate: Date;
  endDate: Date;
}

export interface HardSkills {
  name: string;
  version?: number;
  beginDate?: string;
}

export interface IMetadata {
  createdAt?: Date | string;
  updatedAt?: Date | string;
  owner?: string | null;
  alreadyBloomer?: boolean;
  superBloomer?: boolean;
}

export interface IPositions {
  title?: string;
  url?: string;
  description?: string;
  companyName?: string;
  date1?: string;
  date2?: string;
  roles?: {
    title?: string;
    description?: string;
    date1?: string;
    date2?: string;
  }[];
}

export interface ILinkedInProfile {
  contact: { type: string; values: string[]; links: string[] }[];
  profile: {
    name?: string;
    headline?: string;
    location?: string;
    summary?: string;
    connections?: string;
    followers?: string;
    positions?: IPositions[] | [];
    imageurl?: string;
    languages: { name: string }[];
  };
  languages: { name: string }[];
  positions?: IPositions[] | [];
  skills?: { title: string }[];
  educations?: IPositions[];
  recommendations?: { received: { text: string; name: string }[] };
}

export interface ILinkedInUrl {
  linkedInUrl: string;
  linkedInCredential: ILinkedInCredential;
}

export interface ILinkedInCredential {
  cookies?: string;
  email?: string;
  password?: string;
}

export enum FinanceType {
  pay = 'pay',
  rent = 'rent',
  stack = 'stack',
  CDI = 'CDI',
  RPO = 'RPO',
  freelance = 'freelance',
  IT = 'IT',
  other = 'other',
  People = 'People',
}

export type WinType =
  | 'Freelance Tech & Digital'
  | 'Freelance RPO'
  | 'Freelance Bloomays People'
  | 'CDI Tech & Digital'
  | 'CDI Bloomays People';

export interface INotification {
  channel?: string;
  userGroup?: string;
  recordId?: string;
  title?: string;
  description?: string;
  priority: NotificationPriority;
  read: boolean;
  bloomer?: string[];
  society?: string[];
  activity?: string[];
  mission?: string[];
  canal: NotificationCanal;
  creationDate: string;
  contact: string | null;
  type: NotificationType;
  deleted: boolean;
  canDelete: boolean;
  sent: boolean;
  sentDate?: Date;
  bloomerRecordId?: string;
  missionRecordId?: string;
  link?: string;
}

export interface ILightSlackNotification
  extends Omit<
    INotification,
    'title' | 'priority' | 'read' | 'deleted' | 'creationDate' | 'sent' | 'type' | 'canDelete' | 'contact'
  > {
  userGroup: string;
  channel: string;
}

export interface NotificationJob {
  type: NotificationType;
}

export interface Preferences {
  communicationType: string;
}

export interface MissionFilter {
  recordId?: string;
  month?: string | Date;
  isMissingCRA?: boolean;
}

export interface PipefyJob {
  id: string;
  title: string;
  url: string;
  clientName: string;
}

export interface TalentProcess {
  phase: string;
  salesPriceWithoutTaxes?: number;
  pluses?: string;
  emailResponseAddres?: string;
  emailMessagingAddress?: string;
  referenceAccepted?: string;
  noGoReason?: string;
  noGoExplanation?: string;
}

export interface ContactsInput {
  email: string;
  phoneNumber: string;
}

export enum TemplateContract {
  // sous-traitance
  SST = 'SST',
  // prestation de service
  PDS = 'PDS',
}

export enum CardType {
  candi = 'candi',
  legal = 'legal',
}
export interface WebhookPipefyCardPayload {
  data: {
    action: 'card.create' | 'card.field_update' | 'card.delete';
    card: {
      id: number;
      pipe_id: string;
    };
  };
}

export interface NPSScoring {
  recordId?: string;
  fullName: string;
  email: string;
  created: Date;
  score: number;
  comment?: string;
  collectDate: string;
  source: string;
  activityId: string;
}

export type ActivityFileFields = 'urlCraS3' | 'urlInvoicesS3' | 'urlAdditionalInvoicesS3';

export type AmendmentFromTemplateResult = {
  signNowDocId: string;
  signerId: string;
  smartFields: SmartFields;
  callbackUrl: string;
};
